import type {ReactNode} from 'react';
import {ScrollArea, Table} from '@mantine/core';

export const DIFF_TABLE_FIELD_COLUMN_WIDTH = 250;

/** Below this the two value columns collapse to ~70px and their headers collide. */
const DIFF_TABLE_MIN_WIDTH = 640;

/**
 * Fixed-layout 3-column diff table shell — callers supply rows (composed of
 * `design/atoms/DiffCell`) as children; column widths stay consistent regardless
 * of content length.
 * @registry Fixed-width 3-column table shell (field / original / current) for diff views.
 *
 * Ported from `library-m/data-display/AuditDiffTable` as `DiffTable`. Renamed
 * from `AuditDiffTable` because "Audit" is a banned domain word in `design/`;
 * the exported column-width constant was renamed to
 * `DIFF_TABLE_FIELD_COLUMN_WIDTH` (from `AUDIT_DIFF_FIELD_COLUMN_WIDTH`) for
 * the same reason. Composes `design/atoms/DiffCell`. Other prop names are
 * unchanged.
 */
export interface DiffTableProps {
  /** Rows of the table (already rendered as <Table.Tr>). */
  children: ReactNode;
  /** Column headings (defaults to Field / Previous Value / New Value). */
  fieldHeader?: string;
  originalHeader?: string;
  currentHeader?: string;
}

export const DiffTable = ({
  children,
  fieldHeader = 'Field',
  originalHeader = 'Previous Value',
  currentHeader = 'New Value',
}: DiffTableProps) => {
  return (
    <ScrollArea type="auto">
      <Table
        striped
        highlightOnHover
        withTableBorder
        withColumnBorders
        withRowBorders
        miw={DIFF_TABLE_MIN_WIDTH}
        style={{tableLayout: 'fixed'}}>
        <colgroup>
          <col style={{width: DIFF_TABLE_FIELD_COLUMN_WIDTH}} />
          <col
            style={{
              width: `calc((100% - ${DIFF_TABLE_FIELD_COLUMN_WIDTH}px) / 2)`,
            }}
          />
          <col
            style={{
              width: `calc((100% - ${DIFF_TABLE_FIELD_COLUMN_WIDTH}px) / 2)`,
            }}
          />
        </colgroup>
        <Table.Thead>
          <Table.Tr>
            <Table.Th>{fieldHeader}</Table.Th>
            <Table.Th>{originalHeader}</Table.Th>
            <Table.Th>{currentHeader}</Table.Th>
          </Table.Tr>
        </Table.Thead>
        <Table.Tbody>{children}</Table.Tbody>
      </Table>
    </ScrollArea>
  );
};
